import { defaultRangeExtractor, useVirtualizer, type Range, type Virtualizer } from "@tanstack/react-virtual"
import cx from "classnames"
import { Loader2 } from "lucide-react"
import { observer } from "mobx-react"
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react"
import { providerActionInputFingerprint, matchTaskActionRuns } from "../../actions/actionRuns"
import type { TaskActionRun } from "../../persistence/records"
import { extractRawMessageEvents } from "../../providerEvents/harnessEventTypes"
import type { SessionThreadItem, SessionThreadModel, ThreadContentPart } from "../../store/SessionThreadModel"
import type { TaskSessionDebugInfo } from "../../store/TaskModel"
import { useOptionalCodeStore } from "../../store/context"
import type { TaskTimelineNavigationTarget } from "../../timeline/taskTimelineTypes"
import { InlineMessageRow, InlineMessages, UserInputMessage, prepareInlineMessageRows, type PreparedInlineMessageRow } from "../InlineMessages"
import { type MessageGroup, groupStreamEvents } from "../events/messageGroups"
import { presentedGroupId } from "../events/presentation"
import { ActionRunAnnotation } from "./ActionRunAnnotation"
import { type ThreadActivity, ThreadActivityBar, isActiveThreadStatus } from "./ThreadActivityBar"
import { ThreadImages } from "./ThreadImages"

interface ThreadViewProps {
    model: SessionThreadModel
    taskId: string
    taskSessions?: readonly TaskSessionDebugInfo[]
    disposeOnUnmount?: boolean
    activity?: ThreadActivity | null
    scrollElementRef?: RefObject<HTMLElement | null>
    initialScrollToBottom?: boolean
    timelineNavigationTarget?: TaskTimelineNavigationTarget | null
}

const THREAD_ROW_ESTIMATED_HEIGHT = 96
const THREAD_ROW_OVERSCAN = 12
const THREAD_HISTORY_LOOKAHEAD_VIEWPORTS = 3
const THREAD_HISTORY_PAGE_BUDGET = 3
const THREAD_LIVE_EDGE_THRESHOLD_PX = 32
const NO_ACTION_RUNS: readonly TaskActionRun[] = []

interface PreparedThreadItem {
    item: SessionThreadItem
    messageGroups?: MessageGroup[]
    messageGroupIds?: string[]
    actionRun?: TaskActionRun
}

type ThreadRenderRow =
    | {
          key: string
          type: "item"
          item: Exclude<SessionThreadItem, { type: "provider_events" }>
          actionRun?: TaskActionRun
      }
    | {
          key: string
          type: "provider"
          itemId: string
          actionEventId: string
          row: PreparedInlineMessageRow
          blockStart: boolean
          blockEnd: boolean
      }

type ProviderEventsThreadItem = Extract<SessionThreadItem, { type: "provider_events" }>

interface IdentifiedMessageGroups {
    groups: MessageGroup[]
    ids: string[]
}

const messageGroupsByThreadItem = new WeakMap<ProviderEventsThreadItem, IdentifiedMessageGroups>()

function getThreadItemMessageGroups(item: ProviderEventsThreadItem): IdentifiedMessageGroups {
    const cached = messageGroupsByThreadItem.get(item)
    if (cached) return cached
    const groups = groupStreamEvents(item.events, item.harnessId)
    const rawMessages = extractRawMessageEvents(item.events)
    const terminalSourceId = item.events.findLast((event) => event.direction === "execution" && (event.type === "complete" || event.type === "error"))?.id
    const sourceOccurrences = new Map<string, number>()
    const ids = groups.map((group) => {
        const messageIndex = messageGroupMessageIndex(group)
        const rawSource = messageIndex >= 0 ? rawMessages[messageIndex] : undefined
        const providerIdentity = rawSource ? providerMessageIdentity(rawSource.message) : null
        const sourceId = providerIdentity ? `provider:${providerIdentity}` : `event:${rawSource?.id ?? terminalSourceId ?? item.events.at(-1)?.id ?? item.id}`
        const baseId = `${sourceId}:${messageGroupSourceDiscriminator(group)}`
        const occurrence = sourceOccurrences.get(baseId) ?? 0
        sourceOccurrences.set(baseId, occurrence + 1)
        return occurrence === 0 ? baseId : `${baseId}:${occurrence}`
    })
    const identified = { groups, ids }
    messageGroupsByThreadItem.set(item, identified)
    return identified
}

function providerMessageIdentity(message: unknown): string | null {
    const record = recordValue(message)
    if (!record) return null
    const nestedMessage = recordValue(record.message)
    const item = recordValue(record.item)
    const payload = recordValue(record.payload)
    const update = recordValue(recordValue(record.params)?.update)
    return (
        stringValue(record.uuid) ??
        stringValue(nestedMessage?.uuid) ??
        stringValue(nestedMessage?.id) ??
        stringValue(item?.id) ??
        stringValue(payload?.id) ??
        stringValue(payload?.call_id) ??
        stringValue(update?.toolCallId) ??
        null
    )
}

function recordValue(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function stringValue(value: unknown): string | undefined {
    return typeof value === "string" && value.length > 0 ? value : undefined
}

function messageGroupMessageIndex(group: MessageGroup): number {
    if (group.sourceMessageIndex !== undefined) return group.sourceMessageIndex
    switch (group.type) {
        case "tool":
        case "edit":
        case "write":
        case "bash":
        case "orchestration":
        case "todoWrite":
            return group.messageIndices[0]
        case "fileChange":
        case "system":
        case "result":
        case "unknown":
        case "activity":
        case "providerNotice":
        case "agentActivity":
            return group.messageIndex
        case "text":
        case "thinking":
            return group.endMessageIndex ?? group.messageIndex
        case "stderr":
            return -1
    }
}

function messageGroupSourceDiscriminator(group: MessageGroup): string {
    switch (group.type) {
        case "tool":
        case "edit":
        case "write":
        case "bash":
        case "orchestration":
        case "todoWrite":
            return `${group.type}:${group.toolUseId}`
        case "fileChange":
            return `${group.type}:${group.toolUseId}:${group.changeIndex}`
        case "stderr":
            return `${group.type}:${group.eventId}`
        case "providerNotice":
            return `${group.type}:${group.kind}`
        case "system":
            return `${group.type}:${group.subtype}`
        case "result":
            return `${group.type}:${group.subtype}`
        case "unknown":
            return `${group.type}:${group.originalType ?? "unknown"}`
        case "text":
        case "thinking":
        case "activity":
        case "agentActivity":
            return group.type
    }
}

function threadItemRenderKey(sessionId: string, item: SessionThreadItem): string {
    if (item.type !== "provider_events") return item.id
    const firstEventId = item.events[0]?.id
    return firstEventId ? `${sessionId}:provider-segment:${firstEventId}` : item.id
}

function prepareThreadItems(
    items: readonly SessionThreadItem[],
    actionRuns: readonly TaskActionRun[],
    sessionId: string
): {
    items: PreparedThreadItem[]
    latestPlan: { itemId: string; groupId: string } | null
} {
    let latestPlan: { itemId: string; groupId: string } | null = null
    const userItems = items.filter((item): item is Extract<SessionThreadItem, { type: "user" }> => item.type === "user" && item.kind === "message")
    const matchedActionRuns =
        actionRuns.length === 0
            ? []
            : matchTaskActionRuns(
                  userItems.map((item) => ({
                      clientRequestId: item.clientRequestId,
                      inputFingerprint: providerActionInputFingerprint(threadUserText(item)),
                  })),
                  actionRuns,
                  sessionId
              )
    const actionRunByItemId = new Map(
        userItems.flatMap((item, index) => {
            const run = matchedActionRuns[index]
            return run ? [[item.id, run] as const] : []
        })
    )
    const prepared = items.flatMap((item): PreparedThreadItem[] => {
        if (item.type !== "provider_events") return [{ item, ...(actionRunByItemId.get(item.id) ? { actionRun: actionRunByItemId.get(item.id) } : {}) }]
        const { groups: messageGroups, ids: messageGroupIds } = getThreadItemMessageGroups(item)
        if (messageGroups.length === 0) return []
        messageGroups.forEach((group, index) => {
            if (group.type === "todoWrite") latestPlan = { itemId: item.id, groupId: messageGroupIds[index] ?? presentedGroupId(group, index) }
        })
        return [{ item, messageGroups, messageGroupIds }]
    })
    return { items: prepared, latestPlan }
}

function buildThreadRenderRows(
    preparedThread: ReturnType<typeof prepareThreadItems>,
    sessionId: string,
    taskSessions: readonly TaskSessionDebugInfo[] | undefined,
    completedProviderItemId: string | null
): ThreadRenderRow[] {
    return preparedThread.items.flatMap((preparedItem): ThreadRenderRow[] => {
        const item = preparedItem.item
        if (item.type !== "provider_events") {
            return [
                {
                    key: threadItemRenderKey(sessionId, item),
                    type: "item",
                    item,
                    ...(preparedItem.actionRun ? { actionRun: preparedItem.actionRun } : {}),
                },
            ]
        }

        const inlineRows = prepareInlineMessageRows({
            messageGroups: preparedItem.messageGroups ?? [],
            messageGroupIds: preparedItem.messageGroupIds,
            latestPlanGroupId: preparedThread.latestPlan?.itemId === item.id ? preparedThread.latestPlan.groupId : null,
            taskSessions,
            turnCompleted: completedProviderItemId === item.id,
        })
        return inlineRows.map((row, index) => ({
            key: `${sessionId}:provider:${row.id}`,
            type: "provider",
            itemId: item.id,
            actionEventId: item.id,
            row,
            blockStart: index === 0,
            blockEnd: index === inlineRows.length - 1,
        }))
    })
}

function estimateThreadRowHeight(row: ThreadRenderRow | undefined): number {
    if (!row) return THREAD_ROW_ESTIMATED_HEIGHT
    if (row.type === "item") return row.item.type === "user" ? 132 : 48
    if (row.row.renderable.mode === "inline") return 128
    if (row.row.renderable.mode === "row") return 76
    return 52
}

export const ThreadView = observer(
    ({
        model,
        taskId,
        taskSessions,
        disposeOnUnmount = true,
        activity: suppliedActivity,
        scrollElementRef,
        initialScrollToBottom = false,
        timelineNavigationTarget,
    }: ThreadViewProps) => {
        const codeStore = useOptionalCodeStore()
        const rootRef = useRef<HTMLDivElement>(null)
        const historyDemandBudgetRef = useRef(0)
        const historyDemandReadyRef = useRef(false)
        const historyNativeOffsetRef = useRef(0)
        const initialScrollPendingRef = useRef(initialScrollToBottom)
        const [historyLoadSettledVersion, setHistoryLoadSettledVersion] = useState(0)
        const [resolvedTimelineTargetItemId, setResolvedTimelineTargetItemId] = useState<string | null>(null)
        const [highlightedTimelineTargetItemId, setHighlightedTimelineTargetItemId] = useState<string | null>(null)

        useEffect(() => {
            let cancelled = false
            void model.loadInitial().then(() => {
                if (!cancelled && initialScrollToBottom) initialScrollPendingRef.current = true
            })
            model.startLive()
            return () => {
                cancelled = true
                if (disposeOnUnmount) model.dispose()
            }
        }, [disposeOnUnmount, model])

        const activity = currentThreadActivity(model, suppliedActivity)
        const actionRuns = codeStore?.productState?.tasks.get(taskId)?.actionRuns ?? NO_ACTION_RUNS
        const preparedThread = useMemo(
            () => prepareThreadItems(model.items, actionRuns, model.reference.sessionId),
            [actionRuns, model.items, model.reference.sessionId]
        )
        const completedProviderItemId = completedTurnProviderItemId(model, preparedThread.items, activity)
        const rows = useMemo(
            () => buildThreadRenderRows(preparedThread, model.reference.sessionId, taskSessions, completedProviderItemId),
            [completedProviderItemId, model.reference.sessionId, preparedThread, taskSessions]
        )
        // Keep the single selected row mounted even when it starts outside the
        // virtual range so dynamic measurement can converge on the indexed scroll.
        const timelineTargetItemId = resolvedTimelineTargetItemId ?? highlightedTimelineTargetItemId
        const timelineTargetIndex = timelineTargetItemId ? rows.findIndex((row) => row.type === "item" && row.item.id === timelineTargetItemId) : -1
        const extractThreadRange = useCallback(
            (range: Range) => {
                const indexes = defaultRangeExtractor(range)
                if (timelineTargetIndex < 0 || indexes.includes(timelineTargetIndex)) return indexes
                return [...indexes, timelineTargetIndex].sort((left, right) => left - right)
            },
            [timelineTargetIndex]
        )
        const getScrollElement = useCallback(() => scrollElementRef?.current ?? rootRef.current?.parentElement ?? null, [scrollElementRef])
        const getItemKey = useCallback((index: number) => rows[index]?.key ?? index, [rows])
        const requestOlderHistory = useCallback(() => {
            if (historyDemandBudgetRef.current <= 0 || !model.hasOlder || model.loading || model.olderLoading) return
            historyDemandBudgetRef.current -= 1
            void model.loadOlder().finally(() => setHistoryLoadSettledVersion((version) => version + 1))
        }, [model])
        const handleVirtualizerChange = useCallback(
            (virtualizer: Virtualizer<HTMLElement, HTMLElement>) => {
                const element = virtualizer.scrollElement
                if (!element || !historyDemandReadyRef.current) return
                const atLoadedTop = element.scrollTop === 0
                const viewportHeight = virtualizer.scrollRect?.height ?? element.clientHeight
                if (viewportHeight > 0 && element.scrollTop >= viewportHeight * THREAD_HISTORY_LOOKAHEAD_VIEWPORTS) {
                    historyDemandBudgetRef.current = THREAD_HISTORY_PAGE_BUDGET
                    return
                }
                if (virtualizer.isScrolling) {
                    requestOlderHistory()
                    return
                }
                if (!atLoadedTop) return
                requestOlderHistory()
                if (model.hasOlder && element.scrollHeight > element.clientHeight) {
                    virtualizer.scrollToOffset(1)
                    historyNativeOffsetRef.current = element.scrollTop
                }
            },
            [model, requestOlderHistory]
        )
        const rowVirtualizer = useVirtualizer({
            count: rows.length,
            getScrollElement,
            getItemKey,
            estimateSize: (index) => estimateThreadRowHeight(rows[index]),
            rangeExtractor: extractThreadRange,
            anchorTo: "end",
            followOnAppend: true,
            scrollEndThreshold: THREAD_LIVE_EDGE_THRESHOLD_PX,
            overscan: THREAD_ROW_OVERSCAN,
            directDomUpdates: true,
            useFlushSync: false,
            onChange: handleVirtualizerChange,
        })
        const virtualItems = rowVirtualizer.getVirtualItems()
        const hasOlder = model.hasOlder

        useEffect(() => {
            if (!timelineNavigationTarget || timelineNavigationTarget.anchor.sessionId !== model.reference.sessionId) return
            let cancelled = false
            historyDemandBudgetRef.current = 0
            setResolvedTimelineTargetItemId(null)
            setHighlightedTimelineTargetItemId(null)
            void model
                .loadToHistoryTarget({
                    createdAt: timelineNavigationTarget.createdAt,
                    ...(timelineNavigationTarget.anchor.turnId ? { turnId: timelineNavigationTarget.anchor.turnId } : {}),
                })
                .then((itemId) => {
                    if (!cancelled && itemId) setResolvedTimelineTargetItemId(itemId)
                })
            return () => {
                cancelled = true
            }
        }, [model, timelineNavigationTarget])

        useLayoutEffect(() => {
            if (!resolvedTimelineTargetItemId) return
            const targetIndex = rows.findIndex((row) => row.type === "item" && row.item.id === resolvedTimelineTargetItemId)
            if (targetIndex < 0) return
            initialScrollPendingRef.current = false
            rowVirtualizer.scrollToIndex(targetIndex, { align: "center" })
            setHighlightedTimelineTargetItemId(resolvedTimelineTargetItemId)
            setResolvedTimelineTargetItemId(null)
        }, [resolvedTimelineTargetItemId, rowVirtualizer, rows])

        useEffect(() => {
            const element = getScrollElement()
            if (!element) return
            historyNativeOffsetRef.current = element.scrollTop
            const handleHistoryBoundaryScroll = () => {
                const nextOffset = element.scrollTop
                const reachedLoadedTop = nextOffset === 0 && historyNativeOffsetRef.current > 0
                historyNativeOffsetRef.current = nextOffset
                if (!reachedLoadedTop || !historyDemandReadyRef.current || historyDemandBudgetRef.current > 0) return
                historyDemandBudgetRef.current = THREAD_HISTORY_PAGE_BUDGET
                requestOlderHistory()
            }
            element.addEventListener("scroll", handleHistoryBoundaryScroll, { capture: true, passive: true })
            return () => element.removeEventListener("scroll", handleHistoryBoundaryScroll, { capture: true })
        }, [getScrollElement, requestOlderHistory])

        useEffect(() => {
            const element = getScrollElement()
            if (!element || !historyDemandReadyRef.current) return
            if (!hasOlder) {
                historyDemandBudgetRef.current = 0
                return
            }
            const viewportHeight = rowVirtualizer.scrollRect?.height ?? element.clientHeight
            const scrollOffset = rowVirtualizer.scrollOffset ?? element.scrollTop
            if (viewportHeight <= 0) return
            if (scrollOffset >= viewportHeight * THREAD_HISTORY_LOOKAHEAD_VIEWPORTS) {
                historyDemandBudgetRef.current = THREAD_HISTORY_PAGE_BUDGET
                return
            }
            requestOlderHistory()
        }, [getScrollElement, hasOlder, historyLoadSettledVersion, requestOlderHistory, rowVirtualizer])

        useLayoutEffect(() => {
            if (initialScrollPendingRef.current && rows.length > 0 && !model.loading) {
                rowVirtualizer.scrollToEnd()
                initialScrollPendingRef.current = false
            }
            if (rows.length === 0 || model.loading) return
            historyDemandReadyRef.current = true
        }, [model.loading, rowVirtualizer, rows.length])

        return (
            <div
                ref={rootRef}
                className="flex min-w-0 max-w-full flex-col"
                style={model.hasOlder ? { minHeight: "calc(100% + 1px)" } : undefined}
                data-thread-virtualizer
                data-thread-row-count={rows.length}
            >
                {model.error && <div className="border-b border-border bg-warning/10 px-4 py-2 text-sm text-warning">{model.error}</div>}
                <div ref={rowVirtualizer.containerRef} className="relative min-w-0 max-w-full" data-thread-virtual-content>
                    {virtualItems.map((virtualItem) => {
                        const row = rows[virtualItem.index]
                        if (!row) return null
                        const rowItemId = row.type === "provider" ? row.itemId : row.item.id
                        const timelineTargeted = rowItemId === highlightedTimelineTargetItemId
                        return (
                            <div
                                key={row.key}
                                ref={rowVirtualizer.measureElement}
                                data-index={virtualItem.index}
                                data-thread-item={row.type === "provider" ? row.itemId : row.item.id}
                                data-thread-row={row.key}
                                data-thread-timeline-target={timelineTargeted ? "true" : undefined}
                                className={cx(
                                    "absolute left-0 top-0 min-w-0 max-w-full w-full transition-colors duration-700",
                                    timelineTargeted && "bg-primary/10"
                                )}
                            >
                                {row.type === "provider" ? (
                                    <InlineMessageRow
                                        row={row.row}
                                        taskId={taskId}
                                        taskSessions={taskSessions}
                                        actionEventId={row.actionEventId}
                                        blockStart={row.blockStart}
                                        blockEnd={row.blockEnd}
                                    />
                                ) : (
                                    <ThreadItemView item={row.item} taskId={taskId} taskSessions={taskSessions} actionRun={row.actionRun} />
                                )}
                            </div>
                        )
                    })}
                </div>
                {model.loading && model.items.length === 0 && (
                    <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted">
                        <Loader2 size={14} className="animate-spin" />
                        <span>Loading session...</span>
                    </div>
                )}
                {activity && <ThreadActivityBar key={activity.id} activity={activity} />}
            </div>
        )
    }
)

function currentThreadActivity(model: SessionThreadModel, suppliedActivity?: ThreadActivity | null): ThreadActivity | null {
    if (suppliedActivity !== undefined) return suppliedActivity && isActiveThreadStatus(suppliedActivity.status) ? suppliedActivity : null

    const activeTurnId = model.session?.activeTurnId
    if (activeTurnId) {
        if (model.lastTurnId === activeTurnId && model.lastTurnStatus && isActiveThreadStatus(model.lastTurnStatus)) {
            const startedAt = model.lastTurnStartedAt ?? model.lastTurnQueuedAt
            return { id: activeTurnId, status: model.lastTurnStatus, ...(startedAt ? { startedAt } : {}) }
        }
        return { id: activeTurnId, status: "running" }
    }

    if (model.lastTurnId && model.lastTurnStatus && isActiveThreadStatus(model.lastTurnStatus)) {
        const startedAt = model.lastTurnStartedAt ?? model.lastTurnQueuedAt
        return {
            id: model.lastTurnId,
            status: model.lastTurnStatus,
            ...(startedAt ? { startedAt } : {}),
        }
    }

    const queuedTurnId = model.session?.queuedTurnIds[0]
    if (queuedTurnId) return { id: queuedTurnId, status: "queued" }

    if (model.liveStatus === "streaming" && model.lastTurnId && (!model.lastTurnStatus || isActiveThreadStatus(model.lastTurnStatus))) {
        return { id: model.lastTurnId, status: model.lastTurnStatus ?? "running" }
    }

    return null
}

function completedTurnProviderItemId(model: SessionThreadModel, items: readonly PreparedThreadItem[], activity: ThreadActivity | null): string | null {
    if (activity) return null

    const latestProviderItem = items.findLast(({ item }) => item.type === "provider_events")?.item
    if (latestProviderItem?.type !== "provider_events") return null

    if (model.lastTurnStatus === "completed") {
        const completedTurnItem = model.lastTurnId
            ? items.findLast(({ item }) => item.type === "provider_events" && item.order.turnId === model.lastTurnId)?.item
            : undefined
        return completedTurnItem?.type === "provider_events" ? completedTurnItem.id : latestProviderItem.id
    }

    const session = model.session
    if (session?.status === "idle" && !session.activeTurnId && session.queuedTurnIds.length === 0) {
        return latestProviderItem.id
    }

    return null
}

export const ThreadItemView = memo(function ThreadItemView({
    item,
    taskId,
    taskSessions,
    messageGroups,
    latestPlanGroupId,
    turnCompleted,
    actionRun,
}: {
    item: SessionThreadItem
    taskId: string
    taskSessions?: readonly TaskSessionDebugInfo[]
    messageGroups?: MessageGroup[]
    latestPlanGroupId?: string | null
    turnCompleted?: boolean
    actionRun?: TaskActionRun
}) {
    switch (item.type) {
        case "user":
            return <ThreadUserMessage item={item} taskId={taskId} actionRun={actionRun} />
        case "provider_events":
            return (
                <InlineMessages
                    events={item.events}
                    harnessId={item.harnessId}
                    taskId={taskId}
                    taskSessions={taskSessions}
                    actionEventId={item.id}
                    messageGroups={messageGroups}
                    latestPlanGroupId={latestPlanGroupId}
                    turnCompleted={turnCompleted}
                />
            )
        case "turn_status":
            return (
                <div className="mx-auto w-full max-w-5xl px-3 pb-2 sm:px-5">
                    <div className="border-t border-border pt-2 text-xs text-muted">
                        <span className="font-medium capitalize">{item.status}</span>
                        {item.error && <span className="ml-2 text-warning">{item.error}</span>}
                    </div>
                </div>
            )
    }
})

export function ThreadUserMessage({
    item,
    taskId,
    actionRun,
}: {
    item: Extract<SessionThreadItem, { type: "user" }>
    taskId: string
    actionRun?: TaskActionRun
}) {
    const text = threadUserText(item)
    const images = item.parts.filter((part): part is Extract<ThreadContentPart, { type: "image" }> => part.type === "image")
    if (!text && images.length === 0) return null
    return (
        <UserInputMessage
            text={text}
            taskId={taskId}
            media={<ThreadImages images={images} />}
            annotation={actionRun ? <ActionRunAnnotation run={actionRun} /> : undefined}
            kind={item.kind}
        />
    )
}

function threadUserText(item: Extract<SessionThreadItem, { type: "user" }>): string {
    return item.parts
        .map((part) => partText(part))
        .filter((part): part is string => Boolean(part))
        .join("\n\n")
}

function partText(part: ThreadContentPart): string | null {
    switch (part.type) {
        case "text":
            return part.text
        case "image":
            return null
        case "unknown":
            return "[Unsupported user content]"
    }
}
